<template>
  <div>
    <ag-grid-vue
      style="height: 800px"
      class="ag-theme-material"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :gridOptions="gridOptions"
      :rowData="rowData"
      :resizable="true"
      :sorting="true"
      :filter="true"
      :floatingFilter="true"
      :localeTextFunc="localeTextFunc"
      :suppressRowClickSelection="true"
      :suppressCellSelection="true"
      :suppressContextMenu="true"
      :context="context"
      @grid-ready="onGridReady"
      @first-data-rendered="adjustGrid"
      @row-data-changed="adjustGrid"
      @grid-size-changed="adjustGrid"
    >
    </ag-grid-vue>
    <new-competitor-form
      v-if="competitorTypeOptions"
      v-model="dialogVisible"
      :selectedUrl="selectedUrl"
      :selectedCompetitorGroup="selectedCompetitorGroup"
      :competitor-type-options="competitorTypeOptions"
      @submit="addGroup($event)"
    ></new-competitor-form>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { AgGridVue } from "ag-grid-vue";
import "ag-grid-enterprise";
import { agGridMixin } from "@/mixins/agGridMixin";

import TrafficRenderer from "@/components/gridRenderers/TrafficRenderer";
import CompetitorWebsiteRenderer from "@/pages/Competitors/Grids/Shared/CompetitorWebsiteRenderer";
import CompetitorGroupRenderer from "@/pages/Competitors/Grids/Shared/CompetitorGroupRenderer";
import NewCompetitorForm from "@/pages/Competitors/Grids/Shared/NewCompetitorForm";
import DeleteCompetitorRenderer from "@/pages/Competitors/Grids/Shared/DeleteCompetitorRenderer";

import { RepositoryFactory } from "@/services/repositoryFactory";

const CompetitorsRepository = RepositoryFactory.get("competitorsRepository");

export default {
  mixins: [agGridMixin],
  props: {
    websitesStatistics: Array,
    competitorTypeOptions: Array,
  },
  components: {
    AgGridVue,
    TrafficRenderer,
    CompetitorWebsiteRenderer,
    CompetitorGroupRenderer,
    NewCompetitorForm,
    DeleteCompetitorRenderer,
  },
  data() {
    return {
      rowData: null,
      columnDefs: null,
      defaultColDef: { sortable: true, resizable: true },
      gridApi: null,
      columnApi: null,
      dialogVisible: false,
      selectedUrl: null,
      selectedCompetitorGroup: null,
      gridOptions: { enableRangeSelection: true, copyHeadersToClipboard: true },
    };
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
      this.setColumnDefs();
      if (this.websitesStatistics) this.mapData();
    },
    adjustGrid() {
      if (this.columnApi != null) {
        if (this.columnApi.columnController.scrollWidth > 0) {
          this.gridApi.sizeColumnsToFit();
        }
      }
    },
    setColumnDefs() {
      this.columnDefs = [
        {
          headerName: this.$i18n.t("website"),
          field: "domain",
          minWidth: 140,
          filter: "agTextColumnFilter",
          cellRendererFramework: "CompetitorWebsiteRenderer",
        },
        {
          headerName: this.$i18n.t("traffic"),
          field: "estimatedTraffic",
          width: 130,
          valueFormatter: numberFormatter,
          type: "numericColumn",
          filter: "agNumberColumnFilter",
          filterParams: {
            defaultOption: "greaterThan",
          },
          cellRendererFramework: "TrafficRenderer",
          suppressSizeToFit: true,
          sort: "desc",
        },
        {
          headerName: this.$i18n.t("best_traffic"),
          field: "bestTraffic",
          width: 150,
          valueFormatter: numberFormatter,
          type: "numericColumn",
          filter: "agNumberColumnFilter",
          filterParams: {
            defaultOption: "greaterThan",
          },
          suppressSizeToFit: true,
        },
        {
          headerName: this.$i18n.t("number_of_keywords_on_first_page"),
          field: "nbKeywordTop10",
          width: 120,
          valueFormatter: numberFormatter,
          type: "numericColumn",
          filter: "agNumberColumnFilter",
          filterParams: {
            defaultOption: "greaterThan",
          },
          suppressSizeToFit: true,
        },
        {
          headerName: this.$i18n.t("number_of_keywords_on_first_position"),
          field: "nbKeywordTop1",
          width: 105,
          valueFormatter: numberFormatter,
          type: "numericColumn",
          filter: "agNumberColumnFilter",
          filterParams: {
            defaultOption: "greaterThan",
          },
          suppressSizeToFit: true,
        },
        {
          headerName: this.$i18n.t("competitor_group"),
          field: "competitorGroup",
          headerClass: "ag-centered-header",
          cellClass: "cell-left-aligned",
          width: 170,
          cellRendererFramework: "CompetitorGroupRenderer",
          suppressSizeToFit: true,
        },
        {
          cellRendererFramework: "DeleteCompetitorRenderer",
          cellClass: ["cell-centered"],
          width: 65,
          filter: false,
          suppressMenu: true,
          sorting: false,
        },
      ];
    },
    mapData() {
      this.rowData = this.websitesStatistics.map((website) => {
        if (Object.entries(website.websiteStatisticsByDates).length == 0) {
          return {
            id: website.id,
            domain: website.url.url,
            avgPosition: "--",
            estimatedTraffic: 0,
            pastEstimatedTraffic: 0,
            bestTraffic: "--",
            nbKeywords: "--",
            nbKeywordTop1: "--",
            nbKeywordTop10: "--",
            date: "--",
            isHost: website.isHost,
            competitorGroup: website.competitorGroup,
          };
        }

        const lastKeywordDate = Object.entries(
          website.websiteStatisticsByDates
        ).sort()[Object.entries(website.websiteStatisticsByDates).length - 1];
        const trafficByDates = Object.entries(website.websiteStatisticsByDates)
          .sort()
          .map((statsByDate) => statsByDate[1].estimatedTraffic);
        const bestTraffic = Math.max.apply(Math, trafficByDates);
        return {
          id: website.id,
          domain: website.url.url,
          avgPosition: lastKeywordDate[1].averagePosition,
          estimatedTraffic: parseInt(lastKeywordDate[1].estimatedTraffic),
          pastEstimatedTraffic: trafficByDates[0],
          bestTraffic: bestTraffic,
          nbKeywords: lastKeywordDate[1].numberOfKeywords
            ? lastKeywordDate[1].numberOfKeywords
            : 0,
          nbKeywordTop1: lastKeywordDate[1].positionBucketsStatistics.find(
            (bucket) => bucket.positionMin == 1 && bucket.positionMax == 1
          ).numberOfKeywords,
          nbKeywordTop10: lastKeywordDate[1].positionBucketsStatistics.find(
            (bucket) => bucket.positionMin == 1 && bucket.positionMax == 10
          ).numberOfKeywords,
          date: lastKeywordDate[0],
          isHost: website.isHost,
          competitorGroup: website.competitorGroup,
        };
      });
    },
    setDialogVisible(url, competitorGroup) {
      this.selectedCompetitorGroup = competitorGroup;
      this.selectedUrl = url;
      this.dialogVisible = true;
    },
    addGroup(form) {
      const competitorId = this.rowData.find(
        (competitor) => competitor.domain == form.url
      ).id;
      CompetitorsRepository.updateCompetitorTypeOfCompetitor(
        this.study.id,
        competitorId,
        form.group
      )
        .then((data) => {
          this.$message({
            message: this.$i18n.t("competitor_group_update_success"),
            type: "success",
            duration: 6000,
          });
          this.$emit("refresh-data");
        })
        .catch((error) => {
          this.$message({
            message: this.$i18n.t("competitor_group_update_failure"),
            type: "error",
            duration: 6000,
          });
          console.log(error);
        });
    },
    deleteCompetitor(competitorId) {
      CompetitorsRepository.deleteCompetitor(this.study.id, competitorId)
        .then((data) => {
          this.$message({
            message: this.$i18n.t("competitor_delete_success"),
            type: "success",
            duration: 6000,
          });
          this.$emit("refresh-data");
        })
        .catch((error) => {
          this.$message({
            message: this.$i18n.t("competitor_delete_failure"),
            type: "error",
            duration: 6000,
          });
          console.log(error);
        });
    },
  },
  watch: {
    websitesStatistics() {
      if (this.websitesStatistics) this.mapData();
    },
  },
  beforeMount() {
    this.context = {
      studyId: this.study.id,
      setDialogVisible: this.setDialogVisible,
      deleteCompetitor: this.deleteCompetitor,
    };
  },
  computed: {
    ...mapState(["study"]),
  },
};

let formatNumber = (number) => {
  if (number == "--") return number;
  return Math.floor(number)
    .toString()
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
};

let numberFormatter = (params) => formatNumber(params.value);
</script>

<style lang="scss" scoped>
@use "styles/variables" as *;

::v-deep .cell-centered {
  text-align: center;
}

// ::v-deep .cell-left-aligned {
// 	text-align: center;
// }

::v-deep .ag-centered-header {
  .ag-header-cell-label {
    justify-content: center;
  }
}
</style>

<i18n src="@/javascripts/grid.json"></i18n>
<i18n>
{
  "en": {
    "website": "Website",
    "traffic": "Estimated traffic",
    "vs_j_7_/_vs_j_28": "vs J-7 / vs J-28",
    "best_traffic": "Best estimated traffic",
    "number_of_keywords_on_first_page": "# Top10",
    "number_of_keywords_on_first_position": "# 1st",
    "domain_authority": "Domain authority",
    "referent_domains": "Referring domains",
    "competitor_group": "Group",
    "actions": "Actions",
    "competitor_group_update_success": "The competitor's group has been updated succesfully",
    "competitor_group_update_failure": "Modification of the competitor's group has failed",
    "competitor_delete_success": "The competitor has been deleted succesfully",
    "competitor_delete_failure": "The suppression of the competitor has failed"
  },
  "fr": {
    "website": "Site",
    "traffic": "Trafic estimé",
    "vs_j_7_/_vs_j_28": "vs J-7 / vs J-28",
    "best_traffic": "Meilleur trafic estimé",
    "number_of_keywords_on_first_page": "# Top10",
    "number_of_keywords_on_first_position": "# 1st",
    "domain_authority": "DA",
    "referent_domains": "Domaines référents",
    "competitor_group": "Groupe",
    "actions": "Actions",
    "competitor_group_update_success": "Le groupe du concurrent à été modifié avec succès",
    "competitor_group_update_failure": "Nous avons rencontré une erreur dans la modification du groupe",
    "competitor_delete_success": "Le concurrent à été supprimé avec succès",
    "competitor_delete_failure": "Nous avons rencontré une erreur dans la suppression du concurrent"
  },
  "de": {
    "website": "Grundstück",
    "traffic": "Geschätzter Verkehr",
    "vs_j_7_/_vs_j_28": "gegen J-7 / gegen J-28",
    "best_traffic": "Bester geschätzter Verkehr",
    "number_of_keywords_on_first_page": "#Top 10",
    "number_of_keywords_on_first_position": "#1",
    "domain_authority": "UND",
    "referent_domains": "Verweisende Domains",
    "competitor_group": "Band",
    "actions": "Aktionen",
    "competitor_group_update_success": "Die Gruppe des Wettbewerbers wurde erfolgreich geändert",
    "competitor_group_update_failure": "Beim Ändern der Gruppe ist ein Fehler aufgetreten",
    "competitor_delete_success": "Der Wettbewerber wurde erfolgreich gelöscht",
    "competitor_delete_failure": "Beim Löschen des Mitbewerbers ist ein Fehler aufgetreten"
  }
}
</i18n>
